import React from 'react';
import { Phone, MapPin, Clock, Tag } from 'lucide-react';
import HygieneRatingBadge from '../CanvassingData/HygieneRatingBadge';

const STATUS_BADGES = {
  new: { label: 'New', color: 'bg-gray-100 text-gray-700' },
  contacted: { label: 'Contacted', color: 'bg-blue-100 text-blue-700' },
  interested: { label: 'Interested', color: 'bg-green-100 text-green-700' },
  not_interested: { label: 'Not Interested', color: 'bg-red-100 text-red-700' },
  callback: { label: 'Callback', color: 'bg-amber-100 text-amber-700' },
  converted: { label: 'Converted', color: 'bg-purple-100 text-purple-700' },
  do_not_contact: { label: 'Do Not Contact', color: 'bg-gray-200 text-gray-600' },
};

const LeadCard = ({ lead, onSelect }) => {
  if (!lead) return null;

  const statusBadge = STATUS_BADGES[lead.canvassing_status] || STATUS_BADGES.new;
  const address = [lead.address_line_1, lead.address_town, lead.address_postcode].filter(Boolean).join(', ');

  return (
    <button
      onClick={() => onSelect(lead)}
      className="w-full text-left bg-white rounded-xl shadow-sm border border-gray-200 p-3 active:bg-gray-50 transition-all"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          {/* Status badge + category */}
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${statusBadge.color}`}>
              {statusBadge.label}
            </span>
            {lead.category_name && (
              <span className="flex items-center gap-1 text-xs text-gray-400">
                <Tag size={9} />
                {lead.category_name}
              </span>
            )}
          </div>

          {/* Business name */}
          <h3 className="font-bold text-gray-900 text-sm leading-tight">
            {lead.business_name}
          </h3>

          {/* Address */}
          {address && (
            <div className="flex items-start gap-1 text-xs text-gray-500 mt-1">
              <MapPin size={11} className="mt-0.5 flex-shrink-0" />
              <span className="line-clamp-1">{address}</span>
            </div>
          )}

          {/* Last action time */}
          {lead.canvassed_at && (
            <div className="flex items-center gap-1 text-xs text-gray-400 mt-1">
              <Clock size={10} />
              <span>{(() => {
                const d = new Date(lead.canvassed_at);
                const day = d.getDate();
                const suffix = [11,12,13].includes(day) ? 'th' : {1:'st',2:'nd',3:'rd'}[day%10] || 'th';
                const weekday = d.toLocaleDateString('en-GB', { weekday: 'long' });
                const month = d.toLocaleDateString('en-GB', { month: 'short' });
                const year = d.getFullYear();
                const time = d.toLocaleTimeString('en-GB', { hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase();
                return `${weekday} ${day}${suffix} ${month} ${year}, ${time}`;
              })()}</span>
            </div>
          )}
        </div>

        {/* Right side: hygiene badge + phone icon */}
        <div className="flex flex-col items-end gap-2">
          {lead.fhrs_id && (
            <div onClick={e => e.stopPropagation()}>
              <HygieneRatingBadge lead={lead} />
            </div>
          )}
          {lead.phone_number && (
            <a
              href={`tel:${lead.phone_number}`}
              onClick={e => e.stopPropagation()}
              className="w-10 h-10 rounded-full bg-green-100 text-green-700 flex items-center justify-center"
            >
              <Phone size={16} />
            </a>
          )}
        </div>
      </div>
    </button>
  );
};

export default LeadCard;
